<template>
<div>
  <div class="header">
    <div style="display: flex; flex-direction: row; justify-content: space-between; align-items: center;">
      <div style="width: 70%;">
        <span
          v-if="!precoOriginal && precoLabel"
          style="font-size: 14px; line-height: 14px; display: block;">{{ precoLabel }}</span>
        <span
          v-if="precoOriginal"
          class="preco-original">{{ precoOriginal }}</span>
        <h2 :style="{'color': corPrimaria}">
          {{ preco }}
        </h2>
      </div>
      <div style="width: 30%; text-align: right; font-size: 12px; color: #A8A8A8;">
        <span v-if="isDiscount">À vista</span>
        <span v-else-if="maxInstalments > 1 && showInstalment">Em até {{ maxInstalments }}x no cartão</span>
      </div>
    </div>
  </div>
  <div class="body">
    <input-data-calendario
      v-if="calendarMode"
      v-model="selectedDate"
      :passeio-id="passeioId"
      :initial-available-days="initialAvailableDays"
      :calendar-size="calendarSize"
      @on-open-change="calendarOpened" />
    <input-data-list
      v-else
      v-model="selectedDate"
      :available-days="initialAvailableDays"
      :passeio-id="passeioId"
      :return-days="returnDays"
      :primary-color="corPrimaria"
      @on-height-change="listHeightChanged" />

    <div
      v-show="loadingAvailability"
      style="text-align: center; margin-top: 10px;">
      <i class="fa-regular fa-hourglass" />
    </div>

    <input-tickets
      v-show="!loadingAvailability && selectedDate"
      v-model="selectedAgeRanges"
      style="margin-top: 20px;"
      :options="ageRanges"
      :selected-date="selectedDate"
      :primary-color="corPrimaria"
      :min="quantidadeMinVenda"
      :max="quantidadeMaxVenda"
      :nome-item="$props.nomeItem"
      :moeda="$props.moeda"
      @on-options-open-state-change="ageRangeOpened"
      @on-complience-state-change="complienceChange" />
    <input-horario
      v-show="!loadingAvailability && selectedDate"
      v-model="selectedAvailability"
      style="margin-top: 20px;"
      :horarios="horarios"
      :selected-quantities="selectedAgeRanges"
      :primary-color="corPrimaria"
      :show-remaining-vacancies="showRemainingVacancies"
      :passeio-tipo="tipoPasseio"
      :titulo-atividade="tituloAtividade"
      :genero-titulo-atividade="generoTituloAtividade" />

    <input-opcionais
      v-show="nPessoas > 0"
      v-model="selectedOptionals"
      style="margin-top: 20px;"
      :selected-date="selectedDate"
      :max="nPessoas"
      :primary-color="corPrimaria"
      :mobile-mode="false"
      :passeio-id="parseInt(passeioId)"
      :moeda="$props.moeda"
      @optionals-availability-change="av => hasOptionalsAvailable = av" />

    <submit-button
      style="margin-top: 10px;"
      :disabled="!isReady"
      :is-loading="isSubmitting"
      :label="buttonLabel"
      :primary-color="corPrimaria"
      @click="submit" />

    <alerta-pagamento-parcial
      v-if="$props.hasPaymentPartial"
      :preco-parcial-label="$props.precoParcial"
      :porcentagem-parcial="$props.porcentagemParcial"
      :total="(subtotal + totalOpcionais)"
      :moeda="$props.moeda" />

    <resumo-compra
      v-if="descontoPeriodo > 0 || hasOptionalsAvailable"
      style="margin-top: 25px;"
      :total="totalSemDesconto"
      :opcionais="totalOpcionais"
      :desconto="descontoPeriodo"
      :moeda="$props.moeda" />
  </div>

  <div class="footer">
    <strong
      :key="'subtotal_' + subtotal"
      style="visibility: hidden; display: block;"
      :style="{visibility: isMounted ? 'visible' : 'hidden'}">Subtotal: {{ formatMoney(subtotal + totalOpcionais) }}</strong>
    <span
      v-if="$props.discountByPaymentMethod"
      :key="'totalDesconto_' + totalDesconto"
      style="display: none; color: #2FB97A;"
      :style="{display: (isMounted && totalDesconto < subtotal) ? 'block' : 'none'}">Pagando no {{ discountPaymentMethodsLabel || 'Pix' }}: {{ formatMoney(totalDesconto + totalOpcionais) }}</span>
  </div>
</div>
</template>

<script>
import axios from 'axios';
import api from '@api/store/atividades';

import InputDataCalendario from './components/InputDataCalendario.vue';
import InputDataLista from './components/InputDataLista.vue';
import InputDataList from './components/InputDataList.vue';
import InputHorario from './components/InputHorario.vue';
import InputTickets from './components/InputTickets.vue';
import SubmitButton from './components/SubmitButton.vue';
import ResumoCompra from './components/ResumoCompra.vue';
import InputOpcionais from './components/InputOpcionais.vue';
import AlertaPagamentoParcial from './components/AlertaPagamentoParcial.vue';

const dateToString = function(date) {
  const mes = date.toLocaleDateString('pt-BR', { month: '2-digit' });
  const dia = date.toLocaleDateString('pt-BR', { day: '2-digit' });
  const ano = date.toLocaleDateString('pt-BR', { year: 'numeric' });
  return ano + '-' + mes + '-' + dia;
};

export default {
  components: { InputDataCalendario, InputDataLista, InputHorario, InputTickets, SubmitButton, ResumoCompra, InputOpcionais, AlertaPagamentoParcial, InputDataList},
  props: {
    passeioId: {
      type: String,
      default: () => '0',
      required: true
    },
    corPrimaria: {
      type: String,
      default: () => '#FF0000',
      required: true
    },
    precoOriginal: {
      type: String,
      default: () => '',
      required: true
    },
    preco: {
      type: String,
      default: () => '',
      required: true
    },
    isDiscount: {
      type: Boolean,
      default: () => false,
      required: true
    },
    maxInstalments: {
      type: Number,
      default: () => 0,
      required: true
    },
    showInstalment: {
      type: Boolean,
      default: () => false,
      required: true
    },
    calendarMode: {
      type: Boolean,
      default: () => false,
      required: true
    },
    showRemainingVacancies: {
      type: Boolean,
      default: () => false,
      required: true
    },
    calendarSize: {
      type: Number,
      default: () => 2,
      required: true
    },
    initialAvailableDays: {
      type: String,
      default: () => '[]',
      required: false
    },
    returnDays: {
      type: String,
      default: () => '[]',
      required: true
    },
    quantidadeMinVenda: {
      type: String,
      default: () => '0',
      required: true
    },
    quantidadeMaxVenda: {
      type: String,
      default: () => '9999',
      required: true
    },
    tipoPasseio: {
      type: String,
      default: () => 'fixo',
      required: true
    },
    nomeItem: {
      type: String,
      default: () =>'PESSOAS',
      required: false
    },
    moeda: {
      type: String,
      default: () => 'BRL',
      required: true
    },
    precoLabel: {
      type: String,
      default: () => '',
      required: true
    },
    tituloAtividade: {
      type: String,
      default: () => 'Atividade',
      required: false
    },
    generoTituloAtividade: {
      type: String,
      default: () => 'f',
      required: false
    },
    precoParcial: {
      type: String,
      default: () => 0,
      required: false
    },
    porcentagemParcial: {
      type: Number,
      default: () => 0,
      required: false
    },
    hasPaymentPartial: {
      type: Boolean,
      default: () => false,
      required: true
    },
    discountPaymentMethodsLabel: {
      type: String,
      default: '',
    },
    discountByPaymentMethod: {
      type: Boolean,
      default: () => false,
      required: true
    },
  },
  data() {
    return {
      selectedDate: null,
      selectedAgeRanges: [],
      selectedOptionals: [],
      selectedAvailability: null,
      horarios: [],
      loadingAvailability: false,
      quantityOK: false,
      isMounted: false,
      isSubmitting: false,
      hasOptionalsAvailable: false,
      ageRanges: {}
    };
  },
  computed: {
    isReady() {
      return this.selectedDate !== null &&
        this.quantityOK &&
        this.selectedAvailability !== null &&
        this.selectedAgeRanges.length > 0;
    },
    nPessoas() {
      let n = 0;
      this.selectedAgeRanges.forEach(ticket => n += ticket.quantidade);
      return n;
    },
    totalSemDesconto() { // Soma sem desconto por período nem Pix ou Espécie
      var r = 0.0;
      this.selectedAgeRanges.forEach(faixa => {
        const precoDe = faixa.preco.precoDe != null
          ? Number(faixa.preco.precoDe)
          : Number(faixa.preco.preco);
        r += precoDe * faixa.quantidade;
      });
      return r;
    },
    subtotal() { // Soma dos valores considerando desconto por período, mas sem desconto por pagar à vista
      var r = 0.0;
      this.selectedAgeRanges.forEach(faixa => {
        r += Number(faixa.preco.preco) * faixa.quantidade;
      });
      return r;
    },
    descontoPeriodo() { // Total economizado por ter escolhido um período promocional, sem considerar desconto por pagar à vista
      return this.totalSemDesconto - this.subtotal;
    },
    totalDesconto() { // Total já considerando desconto por período e também por pagar à vista
      var r = 0.0;
      this.selectedAgeRanges.forEach(faixa => {
        const preco = faixa.preco.precoDesconto != null
          ? Number(faixa.preco.precoDesconto)
          : Number(faixa.preco.preco);
        r += preco * faixa.quantidade;
      });
      return r;
    },
    buttonLabel() {
      if (!this.selectedDate) {
        return 'Selecione uma data para continuar';
      }
      if (this.selectedAgeRanges.length === 0 || !this.quantityOK) {
        return 'Selecione a quantidade de pessoas para continuar';
      }
      return 'Reservar agora';
    },
    // Nota: Nenhum dos valores computados acima considera a existencia de opcionais
    totalOpcionais() { // Soma do valor dos opcionais
      var r = 0.0;
      this.selectedOptionals.forEach(op => {
        const preco = Number(op.preco);
        r += preco * op.quantidade;
      });
      return r;
    }
  },
  watch: {
    selectedAgeRange() {
      this.$nextTick(this.emitHeightUpdate);
    },
    selectedDate() {
      this.$nextTick(this.emitHeightUpdate);
      this.selectedAgeRanges = [];
      this.selectedAvailability = null;
      this.syncPricesAndAvailability();
    },
    loadingAvailability() {
      this.$nextTick(this.emitHeightUpdate);
    },
    descontoPeriodo() {
      this.$nextTick(this.emitHeightUpdate);
    },
    isMounted() {
      setTimeout(this.emitHeightUpdate, 100);
    },
    totalOpcionais() {
      this.$nextTick(this.emitHeightUpdate);
    },
  },
  mounted() {
    this.emitHeightUpdate();
    this.isMounted = true;
  },
  methods: {
    resetState() {
      this.selectedDate = null;
      this.selectedAgeRanges = [];
      this.selectedOptionals = [];
      this.selectedAvailability = null;
      this.horarios = [];
      this.loadingAvailability = false;
      this.quantityOK = false;
      this.isSubmitting = false;
      this.hasOptionalsAvailable = false;
      this.ageRanges = {};
    },
    emitHeightUpdate() {
      if(!window.parent) {
        return;
      }
      const calendar = document.getElementById('calendarioVendaApp');
      const event = new CustomEvent('paytCalendarHeightUpdate', { detail: { height: calendar.offsetHeight } });
      window.parent.document.dispatchEvent(event);
    },
    complienceChange(ok) {
      this.quantityOK = ok;
      this.$nextTick(this.emitHeightUpdate);
    },
    listHeightChanged() {
      this.$nextTick(this.emitHeightUpdate);
    },
    calendarOpened() {
      this.$nextTick(this.emitHeightUpdate);
    },
    ageRangeOpened() {
      this.$nextTick(this.emitHeightUpdate);
    },
    formatMoney(price) {
      let locale = 'pt-Br';

      if (this.$props.moeda === 'USD') {
        locale = 'en-US';
      }

      if(price <= 0 || price === null) {
        return Number(0.00).toLocaleString(locale, { style: 'currency', currency: this.$props.moeda });
      }
      return Number(price).toLocaleString(locale, { style: 'currency', currency: this.$props.moeda });
    },
    dataFormatada(data) {
      if(!data) {
        data = new Date();
      }
      const mes = data.toLocaleDateString('pt-BR', { month: '2-digit' });
      const dia = data.toLocaleDateString('pt-BR', { day: '2-digit' });
      const ano = data.toLocaleDateString('pt-BR', { year: 'numeric' });
      return dia + '/' + mes + '/' + ano;
    },
    async syncPricesAndAvailability() {
      this.loadingAvailability = true;
      await this.syncHorarios();
      await this.syncAgeRanges();
      this.loadingAvailability = false;
    },
    async syncHorarios() {
      if(!this.selectedDate) {
        return;
      }
      const data = dateToString(this.selectedDate);
      try {
        const r = await api.listSchedules(this.passeioId, data);
        if(typeof r !== 'object' || Array.isArray(r) || r === null) {
          return;
        }
        const chaves = Object.keys(r);
        const horarios = [];
        chaves.forEach(chave => {
          horarios.push(r[chave]);
        });
        this.horarios = horarios;
      } catch(err) {
        //Empty
      }
    },
    async syncAgeRanges() {
      if(!this.selectedDate) {
        return;
      }
      const data = dateToString(this.selectedDate);
      try {
        const r = await api.listPriceClassifications(this.passeioId, data);
        if(typeof r !== 'object' || Array.isArray(r) || r === null) {
          return;
        }

        this.ageRanges = r;
      } catch(err) {
        //Empty
      }
    },
    emitEventVenda(success = false, errorMsg = null, urlRedirect = null, data = null) {
      if(!window.parent) {
        return;
      }
      const event = new CustomEvent('paytVendaConcluida', {
        detail: {
          success: success,
          redirectTo: urlRedirect,
          data: data,
          error: errorMsg,
        }
      });
      window.parent.document.dispatchEvent(event);
    },
    async submit() {
      if(!this.isReady || this.isSubmitting) {
        return false;
      }
      this.isSubmitting = true;

      const form = new FormData();
      form.append('type', 'passeio');
      form.append('productId', this.passeioId);
      form.append('time', this.selectedAvailability);
      form.append('date', this.dataFormatada(this.selectedDate));

      this.selectedAgeRanges.forEach(preco => {
        form.append('quantity[' + preco.id + ']', preco.quantidade);
      });
      this.selectedOptionals.forEach(opt => {
        form.append('preSelectedOptionals[' + opt.id + ']', opt.quantidade);
      });

      try {
        const r = await axios.post('/adicionar-ao-carrinho', form);
        if(!r || !r.data || !r.data.redirectUrl) {
          this.emitEventVenda(false, 'O servidor não respondeu corretamente');
          return false;
        }
        this.emitEventVenda(true, null, r.data.redirectUrl, {
          idPasseio: this.passeioId,
          idDisponibilidade: this.selectedAvailability,
          data: this.selectedDate,
          tickets: JSON.parse(JSON.stringify(this.selectedAgeRanges))
        });
      } catch {
        this.emitEventVenda(false, 'Falha na conexão com o servidor');
        return false;
      } finally {
        this.isSubmitting = false;
      }

      this.resetState();
      return true;
    }
  }
};
</script>
